import { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams, Link } from "react-router-dom"
import { Calendar, Clock, ArrowRight, TrendingUp, Search, Bookmark, Share2, Eye, MessageCircle, Sparkles } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { loadPublicPosts, loadPublicEvents } from "../store/slices/publicSlice"
import { CATEGORY_LABELS, getCategoryLabel } from "../utils/categoryLabels"
import { getTypeLabel, getStatusLabel } from "../utils/announcementEventUtils"

const CATEGORY_OPTIONS = ["Tous", ...Object.keys(CATEGORY_LABELS)]
const POSTS_LIMIT = 12
const EVENT_TYPE_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "announcement", label: "Annonces" },
  { value: "event", label: "Événements" },
]

export default function Actualite() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get("category") || ""
  const searchParam = searchParams.get("search") || ""
  const authorParam = searchParams.get("author") || ""
  const pageParam = parseInt(searchParams.get("page") || "1", 10)
  const eventTypeParam = searchParams.get("eventType") || ""

  const [selectedCategory, setSelectedCategory] = useState(categoryParam || "Tous")
  const [searchQuery, setSearchQuery] = useState(searchParam)
  const [authorFilter, setAuthorFilter] = useState(authorParam)
  const [page, setPage] = useState(Math.max(1, pageParam))
  const [eventTypeFilter, setEventTypeFilter] = useState(eventTypeParam)
  const isFirstFilterMount = useRef(true)

  const dispatch = useDispatch()
  const { posts, totalPosts, postsPage, postsLimit, events, loading, loadingMore } = useSelector((s) => s.public)
  const limit = postsLimit || POSTS_LIMIT
  const hasMore = posts.length < totalPosts

  useEffect(() => {
    const params = { limit: POSTS_LIMIT, page, sort: "recent" }
    if (selectedCategory && selectedCategory !== "Tous") params.category = selectedCategory
    if (searchQuery.trim()) params.search = searchQuery.trim()
    if (authorFilter.trim()) params.author = authorFilter.trim()
    dispatch(loadPublicPosts(params))
  }, [dispatch, selectedCategory, searchQuery, authorFilter, page])

  useEffect(() => {
    if (isFirstFilterMount.current) {
      isFirstFilterMount.current = false
      return
    }
    setPage(1)
  }, [selectedCategory, searchQuery, authorFilter])

  useEffect(() => {
    const params = { status: "ongoing" }
    if (eventTypeFilter) params.type = eventTypeFilter
    dispatch(loadPublicEvents(params))
  }, [dispatch, eventTypeFilter])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (selectedCategory && selectedCategory !== "Tous") next.set("category", selectedCategory)
    else next.delete("category")
    if (searchQuery.trim()) next.set("search", searchQuery.trim())
    else next.delete("search")
    if (authorFilter.trim()) next.set("author", authorFilter.trim())
    else next.delete("author")
    if (page > 1) next.set("page", String(page))
    else next.delete("page")
    if (eventTypeFilter) next.set("eventType", eventTypeFilter)
    else next.delete("eventType")
    setSearchParams(next, { replace: true })
  }, [selectedCategory, searchQuery, authorFilter, page, eventTypeFilter])

  const heroPost = posts[0]
  const topPosts = posts.slice(1, 4)
  const featuredSection = posts.slice(4, 8)
  const sidebarPosts = posts.slice(8, 12)
  const gridPosts = posts.slice(12)
  const ongoingEventsList = events.slice(0, 5)

  const getImg = (p) => p?.headlineImage?.url ?? p?.headlineImage ?? ""
  const authorName = (p) => p?.author?.fullname || "Auteur"
  const authorId = (p) => p?.author?._id

  return (
    <div className="bg-gradient-to-b from-secondary via-background to-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
          <span>/</span>
          <span className="text-foreground font-medium">Actualités</span>
        </div>

        <div className="mb-8 relative">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary/5 rounded-full blur-2xl" />
          <div className="absolute -top-2 -right-2 w-16 h-16 bg-accent/5 rounded-full blur-xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-3 border border-primary/20">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Dernières Actualités</span>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">Découvrez Nos Actualités</h1>
            <p className="text-sm text-muted-foreground max-w-2xl">Restez informé des dernières nouvelles, événements et réussites de l'Université de Ngozi.</p>
          </div>
        </div>

        {/* Ongoing events and announcements with type filter */}
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Annonces et événements en cours
            </h3>
            <div className="flex items-center gap-2">
              {EVENT_TYPE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value || "all"}
                  onClick={() => setEventTypeFilter(value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${eventTypeFilter === value ? "bg-primary text-primary-foreground shadow-sm" : "bg-card border border-border hover:border-primary/30 hover:bg-primary/5"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {ongoingEventsList.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {ongoingEventsList.map((ev) => (
                <div
                  key={ev._id}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg text-left"
                >
                  <span className="px-2 py-0.5 bg-accent/20 text-accent rounded text-xs font-semibold shrink-0">{getTypeLabel(ev.type)}</span>
                  <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-medium shrink-0">{getStatusLabel(ev.effectiveStatus)}</span>
                  <span className="text-xs font-medium text-foreground">{ev.title}{ev.place ? ` · ${ev.place}` : ""}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Aucune annonce ou événement en cours pour ce filtre.</p>
          )}
        </div>

        {/* Search and filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="relative flex-1 w-full sm:max-w-xs">
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background"
            />
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide w-full sm:w-auto">
            {CATEGORY_OPTIONS.slice(0, 8).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${selectedCategory === cat ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-foreground border border-border hover:border-primary/30 hover:bg-primary/5"}`}
              >
                {cat === "Tous" ? cat : getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {loading && posts.length === 0 ? (
          <div className="animate-pulse space-y-6 py-12">
            <div className="h-72 bg-secondary rounded-2xl" />
            <div className="grid md:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-secondary rounded-xl" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Hero */}
            {heroPost && (
              <article onClick={() => navigate(`/article/${heroPost._id}`)} className="mb-8 group cursor-pointer">
                <div className="bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-500 relative">
                  <div className="grid lg:grid-cols-5 gap-0 relative">
                    <div className="lg:col-span-3 relative h-72 lg:h-96 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 overflow-hidden">
                      {getImg(heroPost) && (
                        <img src={getImg(heroPost)} alt={heroPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-accent text-accent-foreground rounded-lg text-xs font-bold shadow-lg backdrop-blur-sm">
                          <TrendingUp className="w-3.5 h-3.5" />
                          <span>À la Une</span>
                        </div>
                      </div>
                    </div>
                    <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-center">
                      <span className="text-xs font-bold text-primary uppercase tracking-wider mb-3">{getCategoryLabel(heroPost.category)}</span>
                      <h2 className="text-2xl font-bold text-foreground mb-3 leading-tight group-hover:text-primary transition-colors line-clamp-3">{heroPost.title}</h2>
                      <p className="text-sm text-muted-foreground mb-5 leading-relaxed line-clamp-3">{heroPost.excerpt}</p>
                      <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
                        <button onClick={(e) => { e.stopPropagation(); authorId(heroPost) && navigate(`/auteur/${authorId(heroPost)}`); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-bold ring-2 ring-primary/20 ring-offset-2">
                            {authorName(heroPost).charAt(0)}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-semibold text-foreground">{authorName(heroPost)}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(heroPost.publishedAt || heroPost.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                            </div>
                          </div>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/article/${heroPost._id}`); }}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-all group/btn shadow-md border border-primary/20"
                      >
                        Lire l'article
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            )}

            {/* Top 3 cards */}
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {topPosts.map((post) => (
                <article key={post._id} onClick={() => navigate(`/article/${post._id}`)} className="group cursor-pointer">
                  <div className="bg-card rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="relative h-44 bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
                      {getImg(post) && <img src={getImg(post)} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />}
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground rounded-md text-xs font-semibold">{getCategoryLabel(post.category)}</span>
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-sm font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors leading-snug flex-1">{post.title}</h3>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">{post.excerpt}</p>
                      <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-xs font-bold text-primary">
                            {authorName(post).charAt(0)}
                          </div>
                          <span className="text-xs font-medium text-foreground truncate max-w-[100px]">{authorName(post)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{new Date(post.publishedAt || post.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Main + sidebar */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-foreground">Articles Récents</h3>
                  <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">Voir tout →</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {featuredSection.map((post) => (
                    <article
                      key={post._id}
                      onClick={() => navigate(`/article/${post._id}`)}
                      className="group cursor-pointer relative overflow-hidden rounded-2xl h-64 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
                    >
                      {getImg(post) && (
                        <img src={getImg(post)} alt={post.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-60" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-800/20 to-gray-900/60" />
                      <div className="absolute top-4 left-4 z-10">
                        <div className="px-3 py-1.5 bg-background/80 backdrop-blur-md border border-border rounded-lg text-foreground text-xs font-bold shadow-xl">
                          {getCategoryLabel(post.category)}
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/90 via-black/60 to-transparent z-10">
                        <h3 className="text-white font-bold text-base mb-2 line-clamp-2 leading-tight group-hover:text-accent transition-colors">{post.title}</h3>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-white/70">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(post.publishedAt || post.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/article/${post._id}`); }}
                            className="px-3 py-1 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg text-white text-xs font-medium transition-all"
                          >
                            Lire
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <div className="space-y-5">
                <div className="bg-card rounded-xl p-5 border border-border shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-accent" />
                    </div>
                    <h3 className="text-sm font-bold text-foreground">Tendances</h3>
                  </div>
                  <div className="space-y-4">
                    {sidebarPosts.map((post, index) => (
                      <article
                        key={post._id}
                        onClick={() => navigate(`/article/${post._id}`)}
                        className="flex gap-3 group cursor-pointer pb-4 border-b border-border last:border-0 last:pb-0"
                      >
                        <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg overflow-hidden">
                          {getImg(post) && <img src={getImg(post)} alt={post.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
                          <div className="absolute top-1 left-1 w-5 h-5 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xs font-bold">{index + 1}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-bold text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors leading-snug">{post.title}</h4>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span className="truncate">{authorName(post)}</span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent" />
                  <div className="relative p-5 text-white">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <h3 className="text-sm font-bold mb-2">Newsletter UNG</h3>
                    <p className="text-xs mb-4 text-white/90 leading-relaxed">Recevez nos actualités chaque semaine.</p>
                    <button
                      type="button"
                      onClick={() => document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="w-full px-3 py-2 bg-white text-primary rounded-lg text-xs font-semibold hover:bg-white/90 transition-colors shadow-lg"
                    >
                      S'abonner
                    </button>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-5 border border-border shadow-sm">
                  <h3 className="text-sm font-bold text-foreground mb-4">Statistiques</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Articles publiés</span>
                      <span className="text-sm font-bold text-primary">{totalPosts}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Catégories</span>
                      <span className="text-sm font-bold text-foreground">{CATEGORY_OPTIONS.length - 1}</span>
                    </div>
                  </div>
                  <Link to="/auteurs" className="mt-4 block text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                    Tous les auteurs
                  </Link>
                </div>
              </div>
            </div>

            {/* Grid */}
            {gridPosts.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-foreground">Explorer Plus</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {gridPosts.map((post) => (
                    <article
                      key={post._id}
                      onClick={() => navigate(`/article/${post._id}`)}
                      className="group cursor-pointer"
                    >
                      <div className="bg-card rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                        <div className="relative h-40 bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
                          {getImg(post) && <img src={getImg(post)} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <span className="text-xs font-semibold text-primary uppercase mb-2">{getCategoryLabel(post.category)}</span>
                          <h3 className="text-sm font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors leading-snug flex-1">{post.title}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto pt-3 border-t border-border">
                            <span>{new Date(post.publishedAt || post.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {hasMore && (
              <div className="flex items-center justify-center py-8">
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={loadingMore}
                  className="px-6 py-3 rounded-lg border border-primary bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {loadingMore ? "Chargement…" : `Charger plus (${posts.length} / ${totalPosts})`}
                </button>
              </div>
            )}

            {posts.length === 0 && !loading && (
              <div className="text-center py-16 bg-card rounded-xl border border-border">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground">Aucun article trouvé</h3>
                <p className="text-sm text-muted-foreground">Modifiez les filtres ou la recherche.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
